import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import './index.css';
import { assertSupabaseConfigured } from './lib/supabase';
import { completeEmailLinkIfPresent } from './lib/authBootstrap';
import { activeLocaleReady, initI18n } from './i18n';
import { applyBrightness, useThemeStore } from './stores/themeStore';
import { ConfigErrorApp } from './ConfigErrorApp';
import { ErrorBoundary } from './components/ErrorBoundary';

// Port of main.dart's main(): validate config, exchange any PKCE code from
// an email link, init i18n + theme, then mount the app. A config failure
// renders ConfigErrorApp instead of crashing on a blank screen.

async function bootstrap() {
  const root = createRoot(document.getElementById('root')!);

  try {
    assertSupabaseConfigured();
  } catch (e) {
    root.render(<ConfigErrorApp message={String(e instanceof Error ? e.message : e)} />);
    return;
  }

  // Apply the saved brightness before first paint to avoid a flash.
  applyBrightness(useThemeStore.getState().mode);

  // Init i18n with the stored UI locale (null → browser/system detection).
  // English is bundled; any other locale's strings load as their own chunk,
  // in parallel with the email-link exchange below, and are awaited before
  // the App renders (no flash of English).
  initI18n(localStorage.getItem('settings.ui_locale'));
  const i18nReady = activeLocaleReady();

  // Paint a splash immediately. The email-confirmation / magic-link redirect
  // always lands here with a `?code=` we must exchange over the network before
  // the first auth read — without something on screen, that round-trip shows a
  // blank white page (the confirmation already succeeded server-side; this is
  // purely the client landing). A splash turns that gap into a spinner.
  root.render(<BootSplash />);

  // Complete any email-link sign-in (token_hash or PKCE `?code=`) before the
  // first auth read, so a confirmed user lands already signed in instead of on
  // the sign-in screen. Returns true when a signup/email confirmation succeeded
  // this load → seeds the email-confirmed welcome.
  //
  // The App chunk is fetched in parallel with that exchange and the locale
  // bundle; nothing renders until all three are ready, so every component's
  // first useTranslation() sees the initialised i18n singleton.
  const [emailJustConfirmed, { App }] = await Promise.all([
    completeEmailLinkIfPresent(),
    import('./App'),
    i18nReady,
  ]);
  root.render(
    <StrictMode>
      <ErrorBoundary>
        <App emailJustConfirmed={emailJustConfirmed} />
      </ErrorBoundary>
    </StrictMode>,
  );

  // Register the service worker AFTER first paint (so it never delays boot) and
  // start the auto-update polling. autoUpdate reloads the page itself when a
  // new build's worker activates, so users get updates without any refresh.
  void import('./registerPwa').then((m) => m.registerPwa());
}

// Minimal pre-React splash — no i18n / component deps, so it can paint before
// the App chunk loads and during the PKCE exchange. Matches the app background
// (brightness is already applied to <html>).
function BootSplash() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-bg">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-on-surface-variant border-t-transparent" />
    </div>
  );
}

void bootstrap();
